/* ---------------------------------------------------------------------------
   Nepal's national boundary, bundled.

   The register's map draws OpenStreetMap tiles, which need a network. This is
   what it draws when they do not arrive: an outline of the country, from data
   in the app's own bundle, with the structures plotted on it. A bridge
   inspector on a hillside with one bar of signal still gets a map that says
   where things are, rather than an empty grey rectangle — see
   src/map/nepalMap.ts for how the two layers relate.

   PROVENANCE. Natural Earth 1:10m Admin 0 Countries, **Nepal point-of-view
   edition** (`ne_10m_admin_0_countries_nep`), which is public domain —
   https://www.naturalearthdata.com/about/terms-of-use/. The largest ring of
   Nepal's polygon, simplified by Douglas–Peucker at a 0.0126° tolerance (about
   1.4 km) from 1022 points to the 272 below, and rounded to four decimal places
   (about 11 m). That is a cartographic outline at country scale and nothing
   more: it is NOT a survey boundary, and it must not be used to decide what is
   inside Nepal or which side of a line anything falls on.

   Regenerated rather than hand-edited, and the script is in the repository —
   `npm run gen:outline -- --country NPL --pov nep` prints exactly this array.

   ── THIS WAS THE DEFAULT EDITION UNTIL 2026-09-16 ──

   The rule adopted when Pakistan joined is that a tenant's map draws its own
   government's point of view where Natural Earth publishes one, and the data
   file says so — see src/data/pakistan.ts, which carries the whole argument. It
   does publish one for Nepal, and this file was on the default edition, so DoR's
   own register was drawing somebody else's cartography. `GR-34`, now closed.

   WHAT ACTUALLY MOVED, measured between the two source rings rather than
   asserted: **six vertices of 1022**, all of them inside a single patch of the
   far north-west between 80.908°E and 81.003°E, 30.213°N and 30.255°N. The
   furthest departs by 6.65 km. The enclosed ring area goes from 147,167 km² to
   147,228 km² — a difference of about **61 km²**, which is roughly a tenth of a
   per cent of the country and invisible at any zoom this register opens at.

   That is the whole of the change, and it is stated in kilometres rather than in
   names on purpose. This file bundles published cartography for one department's
   offline map; it takes no position on any boundary, and the sentence above is a
   measurement of two files, not a claim about territory.
   --------------------------------------------------------------------------- */

/** One boundary vertex as `[longitude, latitude]`, in degrees. */
export type LngLat = readonly [number, number];

/**
 * The outline, as a closed ring — the last point repeats the first, so a
 * consumer can draw it as a polyline without joining the ends itself.
 */
export const NEPAL_OUTLINE: readonly LngLat[] = [
  [80.976, 30.2552],
  [81.0848, 30.0262],
  [81.1945, 30.0045],
  [81.2273, 30.0225],
  [81.2257, 30.0505],
  [81.2668, 30.0699],
  [81.267, 30.1251],
  [81.3501, 30.1572],
  [81.3779, 30.2326],
  [81.368, 30.301],
  [81.3875, 30.3739],
  [81.5094, 30.3284],
  [81.5368, 30.3786],
  [81.614, 30.4169],
  [81.779, 30.358],
  [81.8444, 30.3712],
  [81.9412, 30.354],
  [81.969, 30.3283],
  [82.0888, 30.3301],
  [82.085, 30.2308],
  [82.1557, 30.1814],
  [82.1276, 30.1244],
  [82.1538, 30.0582],
  [82.2107, 30.0644],
  [82.3189, 30.0318],
  [82.3473, 30.0034],
  [82.3944, 30.0031],
  [82.4571, 29.9541],
  [82.5418, 29.9232],
  [82.6185, 29.8398],
  [82.689, 29.8277],
  [82.691, 29.7807],
  [82.8366, 29.6598],
  [82.8997, 29.6884],
  [82.9319, 29.6642],
  [82.9759, 29.664],
  [83.0649, 29.5788],
  [83.0934, 29.6151],
  [83.1736, 29.6213],
  [83.2608, 29.542],
  [83.2472, 29.4947],
  [83.2639, 29.473],
  [83.328, 29.485],
  [83.3552, 29.4607],
  [83.357, 29.4154],
  [83.3872, 29.3935],
  [83.3931, 29.3562],
  [83.503, 29.261],
  [83.5237, 29.1836],
  [83.6393, 29.1548],
  [83.6955, 29.2262],
  [83.7932, 29.2348],
  [83.9144, 29.2894],
  [83.958, 29.289],
  [83.9758, 29.2637],
  [84.0125, 29.2702],
  [84.0352, 29.2476],
  [84.0897, 29.2566],
  [84.1124, 29.2091],
  [84.1617, 29.1925],
  [84.1323, 29.1438],
  [84.1755, 29.0574],
  [84.2314, 29.026],
  [84.2067, 28.9407],
  [84.2351, 28.9],
  [84.3766, 28.8488],
  [84.4508, 28.7339],
  [84.6162, 28.6937],
  [84.683, 28.6508],
  [84.6778, 28.6044],
  [84.7556, 28.5854],
  [84.812, 28.542],
  [84.9285, 28.5385],
  [85.0549, 28.6387],
  [85.161, 28.595],
  [85.1537, 28.5249],
  [85.0776, 28.4357],
  [85.0874, 28.3041],
  [85.1736, 28.3043],
  [85.2434, 28.2679],
  [85.3126, 28.2828],
  [85.3626, 28.2597],
  [85.4316, 28.2971],
  [85.6118, 28.2511],
  [85.6929, 28.3352],
  [85.6998, 28.2691],
  [85.7345, 28.2215],
  [85.8634, 28.1433],
  [85.8983, 28.036],
  [85.9566, 27.9762],
  [85.9512, 27.9162],
  [85.9803, 27.8852],
  [86.0617, 27.889],
  [86.1129, 27.9268],
  [86.0687, 28.0768],
  [86.156, 28.1565],
  [86.186, 28.1335],
  [86.1722, 28.0718],
  [86.203, 28.0026],
  [86.4253, 27.9099],
  [86.5105, 27.9524],
  [86.5536, 28.1042],
  [86.6123, 28.0803],
  [86.662, 28.1068],
  [86.7317, 28.0769],
  [86.7398, 28.0215],
  [86.8405, 28.0148],
  [86.9147, 27.9451],
  [87.0057, 27.9515],
  [87.1558, 27.8258],
  [87.2905, 27.8161],
  [87.3638, 27.8553],
  [87.3853, 27.8494],
  [87.3691, 27.8039],
  [87.5316, 27.8368],
  [87.5733, 27.805],
  [87.6361, 27.8237],
  [87.7268, 27.8078],
  [87.8367, 27.9083],
  [87.9784, 27.8805],
  [88.03, 27.8933],
  [88.1564, 27.8513],
  [88.1597, 27.7741],
  [88.1109, 27.6395],
  [88.0233, 27.4949],
  [88.0416, 27.4381],
  [88.0148, 27.3781],
  [88.0351, 27.3226],
  [87.9893, 27.2184],
  [87.9692, 27.1108],
  [88.0095, 27.0456],
  [88.0769, 26.9918],
  [88.1553, 26.8456],
  [88.1675, 26.725],
  [88.1017, 26.5819],
  [88.0742, 26.4539],
  [88.0066, 26.3699],
  [87.9755, 26.3666],
  [87.929, 26.3964],
  [87.8698, 26.4646],
  [87.8216, 26.4375],
  [87.769, 26.4515],
  [87.7275, 26.4038],
  [87.6812, 26.4242],
  [87.587, 26.378],
  [87.4803, 26.4234],
  [87.4165, 26.427],
  [87.3564, 26.4037],
  [87.314, 26.3438],
  [87.2453, 26.3702],
  [87.2191, 26.4081],
  [87.1063, 26.4047],
  [87.0667, 26.4656],
  [87.0413, 26.5802],
  [86.876, 26.4949],
  [86.8219, 26.4381],
  [86.7382, 26.4437],
  [86.7136, 26.4146],
  [86.4949, 26.5278],
  [86.316, 26.581],
  [86.2844, 26.612],
  [86.1959, 26.5827],
  [86.0114, 26.6544],
  [85.9521, 26.642],
  [85.8285, 26.5661],
  [85.8097, 26.603],
  [85.781, 26.5992],
  [85.7129, 26.6532],
  [85.7018, 26.7966],
  [85.6094, 26.851],
  [85.4396, 26.7877],
  [85.3859, 26.7884],
  [85.337, 26.7468],
  [85.2873, 26.737],
  [85.1949, 26.7589],
  [85.1657, 26.7863],
  [85.162, 26.851],
  [85.1228, 26.8657],
  [85.0186, 26.8458],
  [85.018, 26.8744],
  [84.9527, 26.8976],
  [84.924, 26.9557],
  [84.8175, 27.0106],
  [84.7607, 26.999],
  [84.6402, 27.0284],
  [84.6215, 27.058],
  [84.6544, 27.1254],
  [84.6577, 27.2034],
  [84.6065, 27.3105],
  [84.2894, 27.3761],
  [84.2255, 27.4404],
  [84.1858, 27.4386],
  [84.1172, 27.5133],
  [84.0796, 27.5095],
  [84.0077, 27.4408],
  [83.8341, 27.434],
  [83.8777, 27.3699],
  [83.8546, 27.3451],
  [83.5904, 27.4566],
  [83.387, 27.4705],
  [83.3559, 27.4525],
  [83.3627, 27.3856],
  [83.3243, 27.3417],
  [83.2824, 27.3309],
  [83.1328, 27.4442],
  [83.0105, 27.4434],
  [82.8763, 27.4875],
  [82.7521, 27.495],
  [82.7297, 27.5182],
  [82.6797, 27.6944],
  [82.6521, 27.7041],
  [82.4407, 27.6664],
  [82.4019, 27.6772],
  [82.3479, 27.726],
  [82.0272, 27.9124],
  [81.9759, 27.917],
  [81.8832, 27.8491],
  [81.8557, 27.8509],
  [81.6889, 27.9632],
  [81.615, 27.9812],
  [81.4735, 28.0664],
  [81.396, 28.1606],
  [81.3579, 28.166],
  [81.3381, 28.1327],
  [81.2962, 28.1283],
  [81.2768, 28.1538],
  [81.2959, 28.1675],
  [81.2243, 28.2508],
  [81.1697, 28.3613],
  [81.0004, 28.397],
  [80.976, 28.4369],
  [80.9053, 28.4408],
  [80.8723, 28.4969],
  [80.7817, 28.5147],
  [80.6786, 28.5747],
  [80.6356, 28.6279],
  [80.5636, 28.647],
  [80.5558, 28.6789],
  [80.5176, 28.68],
  [80.4876, 28.6565],
  [80.4885, 28.5624],
  [80.4084, 28.6263],
  [80.3297, 28.6275],
  [80.291, 28.6897],
  [80.2333, 28.7327],
  [80.1621, 28.7533],
  [80.1142, 28.8026],
  [80.0364, 28.837],
  [80.0303, 28.8777],
  [80.0405, 28.9328],
  [80.1079, 28.9946],
  [80.1326, 29.1102],
  [80.2207, 29.1261],
  [80.2331, 29.1393],
  [80.2188, 29.2111],
  [80.2582, 29.2025],
  [80.2801, 29.3102],
  [80.2495, 29.327],
  [80.2177, 29.4346],
  [80.2575, 29.4501],
  [80.3852, 29.6048],
  [80.3544, 29.7049],
  [80.369, 29.7579],
  [80.4762, 29.8061],
  [80.5713, 29.9469],
  [80.6414, 29.9634],
  [80.7158, 30.0133],
  [80.8298, 30.1171],
  [80.8497, 30.1434],
  [80.8364, 30.17],
  [80.8679, 30.2002],
  [80.976, 30.2552],
];

/**
 * The country's extent, as the outline above gives it.
 *
 * Stated rather than computed at import time: it is the map's home view and the
 * bounds every coordinate in the register is checked against (`npm test`), and
 * both wanted a constant they could be read against rather than a value derived
 * from the same array they are verifying.
 */
export const NEPAL_BOUNDS = {
  minLat: 26.3438,
  maxLat: 30.4169,
  minLng: 80.0303,
  maxLng: 88.1675,
} as const;

/* The tile layers' credits are NOT here. Each base map carries its own, beside
   the URL it belongs to — see TILE_SOURCES in src/map/nepalMap.ts. A credit that
   lived apart from the source it credits is a credit that survives a change of
   provider, which is the one thing it must not do. */
